import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Loader2, Briefcase, Building2, IndianRupee, CalendarDays } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METRIC_META = {
  offers: {
    title: 'Offers released',
    desc: 'Applications that hit the offer_released stage in this period.',
    chip: 'bg-amber-100 text-amber-800',
  },
  joined: {
    title: 'Joined',
    desc: 'Joined applications bucketed by the active basis.',
    chip: 'bg-emerald-100 text-emerald-800',
  },
  pending: {
    title: 'Pending pipeline',
    desc: 'Candidates currently in offer_released — biggest CTC first.',
    chip: 'bg-rose-100 text-rose-800',
  },
};

const fmtINR = (n) => {
  if (n == null) return '—';
  const x = Number(n);
  if (!Number.isFinite(x) || x <= 0) return '—';
  if (x >= 10_000_000) return `₹${(x / 10_000_000).toFixed(2)} Cr`;
  if (x >= 100_000) return `₹${(x / 100_000).toFixed(2)} L`;
  return `₹${x.toLocaleString('en-IN')}`;
};

/**
 * Contextual drill-down dialog for a single (recruiter, metric, period)
 * cell on the Offer vs Joining table. Opens when the user clicks any
 * number in the recruiter row, surfacing the underlying applications.
 *
 * Falls through to the Command Center for an individual row click so
 * recruiters can immediately act on a specific candidate (e.g. nudge a
 * pending offer or open the joining checklist).
 */
const OvjRecruiterDrillDialog = ({
  open,
  onOpenChange,
  recruiterId,
  recruiterName,
  metric,           // 'offers' | 'joined' | 'pending'
  period,           // 'today' | 'week' | 'month' | 'quarter' | 'custom' | 'all'
  startDate,
  endDate,
  joinBasis,
}) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const meta = METRIC_META[metric] || METRIC_META.joined;

  useEffect(() => {
    if (!open || !recruiterId || !metric) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const params = { recruiter_id: recruiterId, metric, period, join_basis: joinBasis || 'stage_event' };
    if (period === 'custom' && startDate) params.start_date = startDate;
    if (period === 'custom' && endDate) params.end_date = endDate;
    axios.get(`${API}/analytics/offer-vs-joining/recruiter-drilldown`, { params })
      .then((res) => { if (!cancelled) setData(res.data || null); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, recruiterId, metric, period, startDate, endDate, joinBasis]);

  const items = data?.items || [];
  const count = data?.count ?? 0;
  const total = data?.ctc_total ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col"
        data-testid="ovj-recruiter-drill-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${meta.chip}`}>
              {meta.title}
            </span>
            <span className="text-slate-900">{recruiterName || 'Recruiter'}</span>
            <Badge variant="outline" className="text-[10px]">
              {count} {count === 1 ? 'candidate' : 'candidates'}
            </Badge>
            <Badge variant="outline" className="text-[10px] font-mono">
              {fmtINR(total)}
            </Badge>
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {meta.desc} {period !== 'all' ? `· basis: ${joinBasis === 'joining_date' ? 'joining date' : 'stage event'}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 pt-1">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading drill-down…
            </div>
          ) : items.length === 0 ? (
            <div className="text-sm text-slate-500 italic py-8 text-center" data-testid="ovj-drill-empty">
              No matching candidates for this slice.
            </div>
          ) : (
            <ul className="space-y-1.5" data-testid="ovj-drill-list">
              {items.map((it) => (
                <li key={it.application_id}>
                  <button
                    type="button"
                    onClick={() => {
                      navigate(`/candidates?view=${it.candidate_id}`);
                      onOpenChange(false);
                    }}
                    className="w-full text-left rounded-md border border-slate-200 hover:border-teal-300 hover:bg-teal-50/30 transition px-3 py-2.5"
                    data-testid={`ovj-drill-row-${it.application_id}`}
                  >
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-slate-900 truncate">
                          {it.candidate_name}
                        </div>
                        <div className="text-xs text-slate-500 truncate flex items-center gap-2 mt-0.5">
                          <Briefcase className="w-3 h-3 flex-shrink-0" /> {it.job_title}
                          {it.client_name && it.client_name !== '—' ? (
                            <>
                              <span className="text-slate-300">·</span>
                              <Building2 className="w-3 h-3 flex-shrink-0" /> {it.client_name}
                            </>
                          ) : null}
                        </div>
                      </div>
                      <div className="text-right text-[11px] text-slate-600 font-mono whitespace-nowrap flex flex-col items-end gap-0.5">
                        <span className="inline-flex items-center gap-1">
                          <IndianRupee className="w-3 h-3" /> {fmtINR(it.offered_ctc).replace('₹','')}
                        </span>
                        {it.primary_date ? (
                          <span className="inline-flex items-center gap-1 text-slate-500">
                            <CalendarDays className="w-3 h-3" /> {it.primary_date}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OvjRecruiterDrillDialog;
